import { useState } from "react";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";

export const QuoteBuilderModal = ({ q, onClose, onSaved }) => {
    const editable = ["requested", "quoted"].includes(q.status);
    const prev = Object.fromEntries((q.quote?.lines || []).map((l) => [l.line_id, l]));
    const [lines, setLines] = useState(q.snapshot.lines.map((l) => ({
        line_id: l.line_id, rate: prev[l.line_id]?.rate ?? l.ref_rate ?? 0,
        available: prev[l.line_id]?.available ?? true, note: prev[l.line_id]?.note ?? "",
    })));
    const [discount, setDiscount] = useState(q.quote?.discount ?? 0);
    const [validity, setValidity] = useState(7);
    const [notes, setNotes] = useState(q.quote?.notes ?? "");
    const [busy, setBusy] = useState(false);
    const set = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

    const sub = q.snapshot.lines.reduce((s, l, i) => s + (lines[i].available ? l.qty * (Number(lines[i].rate) || 0) : 0), 0);
    const tax = q.snapshot.lines.reduce((s, l, i) => s + (lines[i].available ? l.qty * (Number(lines[i].rate) || 0) * (l.tax_percent || 0) / 100 : 0), 0);
    const total = sub + tax - (Number(discount) || 0);

    const save = async (send) => {
        setBusy(true);
        try {
            await api.put(`/admin/planner/quotes/${q.id}`, {
                lines: lines.map((l) => ({ ...l, rate: Number(l.rate) || 0 })),
                discount: Number(discount) || 0, validity_days: Number(validity) || 7, notes, send,
            });
            toast.success(send ? "Quotation sent to customer" : "Draft saved");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="planner-quote-builder">
            <div className="w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-white rounded-xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between">
                    <div><div className="font-black text-lg">{q.number} · {q.client_name}</div>
                        <div className="text-xs text-slate-500">{q.planner_type} · {q.price_type} rates · delivery: {q.delivery_preference || "—"}{q.notes && ` · “${q.notes}”`}</div>
                        {q.first_order_eligible && <div className="text-xs text-emerald-700 font-bold mt-1">First-order customer — eligible for ₹500 discount if the offer applies.</div>}</div>
                    <button onClick={onClose} className="text-slate-400" data-testid="planner-quote-builder-close"><X size={18}/></button>
                </div>
                <table className="hdb-table w-full text-sm mt-4">
                    <thead><tr><th className="text-left">Item requested</th><th className="text-left">Preferences</th><th className="text-right">Qty</th>
                        <th className="text-right">GST</th><th className="text-right">Rate ₹</th><th>Available</th><th className="text-left">Note</th></tr></thead>
                    <tbody>{q.snapshot.lines.map((l, i) => (
                        <tr key={l.line_id} data-testid={`planner-qb-line-${i}`}>
                            <td><div className="font-semibold">{l.name}</div>
                                {l.match_status !== "matched" && <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 rounded">NOT IN ITEM MASTER</span>}</td>
                            <td className="text-xs text-slate-500">{[l.brand_preference && `Brand ${l.brand_preference}`, l.alt_brands && `Alt ${l.alt_brands}`,
                                l.pack_size && `${l.pack_size} ${l.uom}`, l.frequency, l.department, !l.substitution_allowed && "No substitutes", l.notes].filter(Boolean).join(" · ")}</td>
                            <td className="hdb-num hdb-num-right">{l.qty}</td>
                            <td className="hdb-num hdb-num-right">{l.tax_percent || 0}%</td>
                            <td><input type="number" min="0" step="0.01" className="hdb-input !w-24 text-right hdb-mono" disabled={!editable}
                                       value={lines[i].rate} onChange={(e) => set(i, "rate", e.target.value)} data-testid={`planner-qb-rate-${i}`}/></td>
                            <td className="text-center"><input type="checkbox" disabled={!editable} checked={lines[i].available}
                                                               onChange={(e) => set(i, "available", e.target.checked)} data-testid={`planner-qb-avail-${i}`}/></td>
                            <td><input className="hdb-input !w-40 text-xs" disabled={!editable} value={lines[i].note}
                                       onChange={(e) => set(i, "note", e.target.value)} placeholder="e.g. 5 kg pack" data-testid={`planner-qb-note-${i}`}/></td>
                        </tr>))}</tbody>
                </table>
                <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                    <div className="flex gap-3 flex-wrap">
                        <label><span className="hdb-label">Discount ₹</span><input type="number" min="0" className="hdb-input !w-28 hdb-mono" disabled={!editable}
                               value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="planner-qb-discount"/></label>
                        <label><span className="hdb-label">Valid for (days)</span><input type="number" min="1" className="hdb-input !w-24 hdb-mono" disabled={!editable}
                               value={validity} onChange={(e) => setValidity(e.target.value)} data-testid="planner-qb-validity"/></label>
                        <label><span className="hdb-label">Note to customer</span><input className="hdb-input !w-72" disabled={!editable}
                               value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="planner-qb-notes"/></label>
                    </div>
                    <div className="text-right text-sm">
                        <div>Subtotal <b className="hdb-mono">{fmt(sub)}</b> · GST <b className="hdb-mono">{fmt(tax)}</b></div>
                        <div className="text-lg font-black">Total <span className="hdb-mono" data-testid="planner-qb-total">{fmt(total)}</span></div>
                        {total < 4000 && <div className="text-xs text-red-700">Below ₹4,000 minimum — customer can't confirm this quote.</div>}
                    </div>
                </div>
                {editable && (
                    <div className="mt-4 flex justify-end gap-2">
                        <button className="hdb-btn hdb-btn-ghost" disabled={busy} onClick={() => save(false)} data-testid="planner-qb-save">Save draft</button>
                        <button className="hdb-btn hdb-btn-primary" disabled={busy} onClick={() => save(true)} data-testid="planner-qb-send">
                            {busy && <Loader2 size={13} className="animate-spin"/>} Send to customer</button>
                    </div>
                )}
            </div>
        </div>
    );
};
